import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, Megaphone, MessageCircle, Pin, Pencil, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phase F.3 — Posts & Inquiries admin page.
 *
 * Two tabs:
 *   1. Posts — admin CRUD for portal notice-board posts (pinned + feed,
 *      audience = all_clients | all_employees | specific_*).
 *   2. Inquiry Topics — admin manages communication prompts surfaced in
 *      client and employee portals (separate sub-tabs).
 */

const POST_IDEAS = [
  "Holiday hours / closed dates",
  "E-transfer payment instructions",
  "Service area expansion",
  "Welcome / onboarding info",
  "Weather delays or cancellations",
  "Insurance & policy reminders",
  "New service offerings",
  "Year-end statements available",
  "Safety reminder",
];

export default function PostsAndInquiriesAdmin() {
  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-2xl font-bold">Posts & Inquiries</h1>
        <p className="text-sm text-muted-foreground">
          Manage the notice board posts visible in client / employee portals, plus the inquiry prompts surfaced in their Communications tab.
        </p>
      </div>
      <Tabs defaultValue="posts">
        <TabsList>
          <TabsTrigger value="posts" data-testid="admin-tab-posts"><Megaphone className="h-4 w-4 mr-1" /> Posts</TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries"><MessageCircle className="h-4 w-4 mr-1" /> Inquiry Topics</TabsTrigger>
        </TabsList>
        <TabsContent value="posts"><PostsManager /></TabsContent>
        <TabsContent value="inquiries"><InquiryTopicsManager /></TabsContent>
      </Tabs>
    </div>
  );
}

function PostsManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { id: null, title: "", body_html: "", audience: "all_clients", audience_ids: [], pinned: false, expires_at: "" };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/portal-posts`);
      setPosts(res.data?.posts || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing({ ...blank }); setEditorOpen(true); };
  const openEdit = (p) => {
    setEditing({ ...p, audience_ids: p.audience_ids || [], expires_at: p.expires_at ? p.expires_at.slice(0, 10) : "" });
    setEditorOpen(true);
  };

  const save = async () => {
    try {
      const body = {
        title: editing.title.trim(),
        body_html: editing.body_html,
        audience: editing.audience,
        audience_ids: editing.audience_ids || [],
        pinned: !!editing.pinned,
        expires_at: editing.expires_at ? new Date(editing.expires_at).toISOString() : null,
      };
      if (editing.id) {
        await axios.put(`${API}/portal-posts/${editing.id}`, body);
      } else {
        await axios.post(`${API}/portal-posts`, body);
      }
      toast.success("Post saved");
      setEditorOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await axios.delete(`${API}/portal-posts/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle>Notice Board Posts</CardTitle>
            <CardDescription>
              Sticky pinned notice + scrollable feed. Use this for company broadcasts, payment instructions, holiday hours, weather delays, etc.
            </CardDescription>
          </div>
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-post-btn"><Plus className="h-4 w-4 mr-1" /> New Post</Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
         posts.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="admin-posts-empty">No posts yet. Click <strong>New Post</strong> to broadcast something.</p> :
        <div className="space-y-2" data-testid="admin-posts-list">
          {posts.map((p) => (
            <div key={p.id} className="p-3 border rounded-lg flex items-center justify-between gap-2 flex-wrap" data-testid={`admin-post-${p.id}`}>
              <div>
                <p className="font-medium flex items-center gap-2">
                  {p.pinned && <Pin className="h-3.5 w-3.5 text-amber-600" />}
                  {p.title}
                  <Badge variant="outline">{p.audience.replace(/_/g, " ")}</Badge>
                </p>
                <p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}{p.expires_at ? ` · expires ${new Date(p.expires_at).toLocaleDateString()}` : ""}</p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`admin-post-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(p.id)} data-testid={`admin-post-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>}

        <details className="mt-4 text-xs">
          <summary className="cursor-pointer text-muted-foreground">Need ideas? Click for examples</summary>
          <ul className="mt-2 ml-4 list-disc text-muted-foreground">
            {POST_IDEAS.map((idea, i) => <li key={i}>{idea}</li>)}
          </ul>
        </details>
      </CardContent>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl" data-testid="post-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Post" : "New Post"}</DialogTitle>
            <DialogDescription>Posts appear in the Posts tab of the chosen audience's portal.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="post-editor-title" />
              </div>
              <div>
                <Label>Body (HTML supported — line breaks become paragraphs in plain text)</Label>
                <Textarea rows={8} value={editing.body_html} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} data-testid="post-editor-body" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Audience</Label>
                  <Select value={editing.audience} onValueChange={(v) => setEditing({ ...editing, audience: v })}>
                    <SelectTrigger data-testid="post-editor-audience"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_clients">All clients</SelectItem>
                      <SelectItem value="all_employees">All employees</SelectItem>
                      <SelectItem value="specific_clients">Specific clients</SelectItem>
                      <SelectItem value="specific_employees">Specific employees</SelectItem>
                      <SelectItem value="specific_users">Specific portal users</SelectItem>
                    </SelectContent>
                  </Select>
                  {editing.audience.startsWith("specific_") && (
                    <Input className="mt-2" placeholder="Comma-separated IDs"
                           value={(editing.audience_ids || []).join(",")}
                           onChange={(e) => setEditing({ ...editing, audience_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                           data-testid="post-editor-ids" />
                  )}
                </div>
                <div>
                  <Label>Expires (optional)</Label>
                  <Input type="date" value={editing.expires_at || ""} onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })} data-testid="post-editor-expires" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer" htmlFor="post-pinned">
                <Switch id="post-pinned" checked={!!editing.pinned} onCheckedChange={(v) => setEditing({ ...editing, pinned: v })} data-testid="post-editor-pinned" />
                <span className="text-sm">Pin this post to the top of the feed</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="post-editor-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


function InquiryTopicsManager() {
  return (
    <Tabs defaultValue="clients">
      <TabsList>
        <TabsTrigger value="clients" data-testid="inquiry-aud-clients">Client topics</TabsTrigger>
        <TabsTrigger value="employees" data-testid="inquiry-aud-employees">Employee topics</TabsTrigger>
      </TabsList>
      <TabsContent value="clients"><TopicsForAudience audience="clients" /></TabsContent>
      <TabsContent value="employees"><TopicsForAudience audience="employees" /></TabsContent>
    </Tabs>
  );
}

function TopicsForAudience({ audience }) {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inquiry-topics?audience=${audience}`);
      setTopics(res.data?.topics || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [audience]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const lbl = newLabel.trim();
    if (!lbl) return;
    try { await axios.post(`${API}/inquiry-topics`, { audience, label: lbl, sort_order: topics.length, is_active: true }); setNewLabel(""); load(); }
    catch (e) { toast.error("Failed to add"); }
  };
  const update = async (t, patch) => {
    try { await axios.put(`${API}/inquiry-topics/${t.id}`, { audience, label: patch.label ?? t.label, is_active: patch.is_active ?? t.is_active, sort_order: patch.sort_order ?? t.sort_order }); load(); }
    catch (e) { toast.error("Failed to update"); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this topic?")) return;
    try { await axios.delete(`${API}/inquiry-topics/${id}`); load(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{audience === "clients" ? "Client portal" : "Employee portal"} inquiry topics</CardTitle>
        <CardDescription>
          These are the prompts portal users see in their Communications tab. Toggle off to hide a default; click the trash to remove a custom one.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="space-y-1" data-testid={`inquiry-list-${audience}`}>
            {topics.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-2 p-2 border rounded-md" data-testid={`inquiry-row-${t.id}`}>
                <div className="flex items-center gap-2 flex-1">
                  <Switch checked={!!t.is_active} onCheckedChange={(v) => update(t, { is_active: v })} data-testid={`inquiry-active-${t.id}`} />
                  <Input value={t.label} onChange={(e) => setTopics(topics.map((x) => x.id === t.id ? { ...x, label: e.target.value } : x))}
                         onBlur={() => update(t, { label: t.label })} className="flex-1" data-testid={`inquiry-label-${t.id}`} />
                  {t.is_custom && <Badge variant="outline">custom</Badge>}
                </div>
                {t.is_custom && (
                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => del(t.id)} data-testid={`inquiry-del-${t.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 pt-1">
          <Input placeholder="Add a topic…" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} data-testid={`inquiry-add-input-${audience}`} />
          <Button onClick={add} variant="outline" data-testid={`inquiry-add-btn-${audience}`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
